// Metadata estática por ruta (nombre de la ruta en src/router/index.js).
// Las vistas con datos dinámicos (detalles/:id, catalogo filtrado) sobreescriben
// estos valores llamando a useSeoMeta() de nuevo una vez que cargan sus datos.
//
// `path` + `sitemap` son la fuente de verdad de qué rutas públicas van en
// sitemap.xml (ver getSitemapStaticRoutes más abajo): scripts/generate-sitemap.mjs
// las lee de aquí en vez de mantener una lista aparte. Una ruta indexable sin
// `path`/`sitemap` (como "detalles", que es dinámica) simplemente no aporta
// una entrada estática al sitemap; una ruta noindex (favoritos, About) nunca
// debe llevar estos campos.

export const routesMeta = {
  HomeV2Preview: {
    title: "Vista previa del nuevo inicio",
    description: "Vista previa interna de la nueva página de inicio de CAR4U.",
    robots: "noindex, nofollow",
  },

  HomeLegacy: {
    title: "Inicio anterior",
    description: "Versión anterior de la página de inicio de CAR4U.",
    robots: "noindex, nofollow",
    canonicalPath: "/",
  },

  Home: {
    path: "/",
    title: "CAR4U | Autos Seminuevos Certificados con Garantía en México",
    description:
      "Compra tu auto seminuevo certificado en CAR4U. Miles de opciones, financiamiento a tu medida y garantía en motor y transmisión. Encuentra tu próximo auto hoy.",
    robots: "index, follow",
    sitemap: { changefreq: "daily", priority: "1.0" },
  },

  catalogo: {
    // Ruta base sin filtros: la única vista de catálogo indexable (ver
    // isCatalogIndexable). Los filtros usan el mismo nombre de ruta pero con
    // otros params, así que no se listan aparte aquí.
    path: "/catalogo/0/0/0",
    title: "Catálogo de Autos Seminuevos en Venta",
    description:
      "Explora nuestro catálogo de autos seminuevos certificados. Filtra por marca, modelo, año, color y precio. Financiamiento disponible y garantía incluida.",
    robots: "index, follow",
    sitemap: { changefreq: "daily", priority: "0.9" },
  },

  detalles: {
    // Dinámica (una URL por vehículo): scripts/generate-sitemap.mjs las agrega
    // por separado consultando la API, no vía path/sitemap aquí.
    // Fallback mientras carga el detalle del vehículo o si la API no responde.
    title: "Ficha de Vehículo Seminuevo",
    description:
      "Consulta precio, kilometraje, equipamiento y fotos de este auto seminuevo certificado por CAR4U, con opciones de financiamiento y garantía.",
    robots: "index, follow",
  },

  contacto: {
    path: "/contacto",
    title: "Contacto",
    description:
      "¿Tienes dudas sobre tu próximo auto seminuevo? Contáctanos por WhatsApp, teléfono o correo. Nuestros asesores en CAR4U están para ayudarte.",
    robots: "index, follow",
    sitemap: { changefreq: "monthly", priority: "0.6" },
  },

  recomendaciones: {
    path: "/recomendaciones",
    title: "Sucursales CAR4U | Encuentra tu Agencia de Autos Seminuevos",
    description:
      "Conoce nuestras sucursales en Aguascalientes y el interior de la República. Visítanos y encuentra el auto seminuevo ideal para ti.",
    robots: "index, follow",
    sitemap: { changefreq: "monthly", priority: "0.6" },
  },

  blog: {
    path: "/blog",
    title: "Blog CAR4U | Noticias y Consejos sobre Autos Seminuevos",
    description:
      "Entérate de las últimas noticias, promociones y consejos para comprar tu auto seminuevo en CAR4U.",
    robots: "index, follow",
    sitemap: { changefreq: "weekly", priority: "0.5" },
  },

  favoritos: {
    title: "Tus Autos Favoritos",
    description: "Consulta los vehículos que has guardado como favoritos en CAR4U.",
    // Contenido privado/por usuario (localStorage): no aporta valor en
    // buscadores. Sin path/sitemap a propósito: nunca debe listarse.
    robots: "noindex, follow",
  },

  About: {
    title: "Nosotros",
    description:
      "Conoce más sobre CAR4U, la distribuidora de autos seminuevos con presencia en todo el país.",
    // Página huérfana con contenido equivalente a /recomendaciones: se evita
    // el duplicado apuntando su canonical ahí y marcándola noindex. Sin
    // path/sitemap a propósito: nunca debe listarse.
    robots: "noindex, follow",
    canonicalPath: "/recomendaciones",
  },
};

// Única fuente de verdad para las rutas públicas estáticas del sitemap:
// cualquier entrada indexable con path+sitemap definidos aparece aquí
// automáticamente, sin mantener una lista aparte en generate-sitemap.mjs.
export function getSitemapStaticRoutes() {
  return Object.values(routesMeta)
    .filter((route) => route.robots === "index, follow" && route.path && route.sitemap)
    .map((route) => ({
      path: route.path,
      changefreq: route.sitemap.changefreq,
      priority: route.sitemap.priority,
    }));
}

// Una ruta /catalogo/:op/:id/:id2 sin filtro (op 0 o ausente) es la única
// vista de catálogo que conviene indexar; el resto son combinaciones de
// filtros (miles de URLs posibles) que se marcan noindex para evitar
// contenido duplicado/fino, y canonicalizan a la vista base.
export function isCatalogIndexable(params = {}) {
  return !params.op || params.op === "0";
}

// Etiquetas cortas para BreadcrumbList (Inicio > Etiqueta). La ruta
// "detalles" arma su propio breadcrumb con el nombre real del vehículo.
export const breadcrumbLabels = {
  catalogo: "Catálogo de autos",
  contacto: "Contacto",
  recomendaciones: "Sucursales",
  blog: "Blog",
  favoritos: "Favoritos",
  About: "Nosotros",
};

export default routesMeta;
